'use client';

import { useState } from 'react';
import { Eye, EyeOff, LogIn, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// Usuarios de demostración (chips opcionales para autocompletar en el demo).
const DEMO = [
  { cedula: '4001', rol: 'Técnico administrativo' },
  { cedula: '4004', rol: 'Contratista de instrucción' },
  { cedula: '4003', rol: 'Abogado especializado' },
  { cedula: '1001', rol: 'Director de instrucción' },
];

// Forma del usuario que devuelve el backend en /auth/login.
export interface Sesion {
  id: string;
  nombre: string;
  numeroDocumento: string;
  rol: string;
  area?: string;
}

interface RespLogin {
  accessToken: string;
  usuario: Sesion;
}

export default function Login({
  onLogin,
  onPortal,
}: {
  onLogin: (s: Sesion) => void;
  onPortal?: () => void;
}) {
  const [modo, setModo] = useState<'login' | 'recuperar'>('login');
  const [cedula, setCedula] = useState('');
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);

  async function ingresar(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!cedula.trim() || !clave.trim()) {
      setError('Ingrese su número de cédula y contraseña.');
      return;
    }
    setCargando(true);
    try {
      const data = await api<RespLogin>('/auth/login', {
        method: 'POST',
        auth: false,
        body: { numeroDocumento: cedula.trim(), password: clave },
      });
      // Persistimos sesión para llamadas autenticadas y recargas.
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('usuario', JSON.stringify(data.usuario));
      onLogin(data.usuario);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo iniciar sesión.';
      setError(msg);
      toast.error('No se pudo iniciar sesión', { description: msg });
    } finally {
      setCargando(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Panel de marca (verde institucional) */}
      <aside className="relative hidden flex-col items-center justify-center gap-6 overflow-hidden bg-sidebar p-10 text-center text-sidebar-foreground lg:flex">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[length:20px_20px]"
        />

        <span className="relative z-10 flex size-32 items-center justify-center rounded-full bg-white shadow-xl">
          <img
            src="/assets/img/escudo-risaralda-disciplinario.png"
            alt="Escudo institucional"
            className="size-24 object-contain"
          />
        </span>

        <div className="relative z-10 max-w-sm space-y-3">
          <h2 className="text-2xl font-bold tracking-tight">Módulo Disciplinario</h2>
          <p className="text-sm leading-relaxed text-sidebar-foreground/85">
            Gestione procesos, actuaciones, decisiones y reportes disciplinarios
            desde un solo lugar, con trazabilidad y control de términos legales.
          </p>
        </div>

        <span className="relative z-10 rounded-full border border-sidebar-foreground/20 bg-sidebar-foreground/10 px-4 py-1.5 text-xs font-semibold">
          Ley 1952 de 2019 y Ley 2094 de 2021
        </span>

        <span className="relative z-10 text-xs text-sidebar-foreground/70">
          Sistema de Gestión Disciplinaria
        </span>
      </aside>

      {/* Panel de formulario */}
      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md">
          {/* Marca visible en móvil */}
          <div className="mb-6 flex items-center justify-center gap-3 lg:hidden">
            <span className="flex size-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-border">
              <img
                src="/assets/img/escudo-risaralda-disciplinario.png"
                alt="Escudo institucional"
                className="size-8 object-contain"
              />
            </span>
            <span className="text-lg font-semibold tracking-tight">Módulo Disciplinario</span>
          </div>

          <Card>
            {modo === 'login' ? (
              <>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <LogIn className="size-5 text-primary" /> Iniciar sesión
                  </CardTitle>
                  <CardDescription>
                    Ingrese su número de cédula y contraseña.
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <form onSubmit={ingresar} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cedula">Usuario (número de cédula)</Label>
                      <div className="relative">
                        <User className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="cedula"
                          type="text"
                          inputMode="numeric"
                          autoComplete="username"
                          placeholder="Ej: 1090000111"
                          className="pl-9"
                          value={cedula}
                          onChange={(e) => {
                            setCedula(e.target.value);
                            setError('');
                          }}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="clave">Contraseña</Label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="clave"
                          type={verClave ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder="Su contraseña"
                          className="px-9"
                          value={clave}
                          onChange={(e) => {
                            setClave(e.target.value);
                            setError('');
                          }}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setVerClave((v) => !v)}
                          aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        >
                          {verClave ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </button>
                      </div>
                    </div>

                    {error && (
                      <p className="text-sm font-medium text-destructive">{error}</p>
                    )}

                    <Button type="submit" className="w-full" disabled={cargando}>
                      <LogIn className="size-4" />
                      {cargando ? 'Ingresando…' : 'Ingresar'}
                    </Button>

                    <div className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        className="text-muted-foreground"
                        onClick={() => {
                          setModo('recuperar');
                          setError('');
                        }}
                      >
                        Recuperar contraseña
                      </Button>
                    </div>
                  </form>

                  <div className="mt-6 space-y-2 border-t pt-4">
                    <p className="text-xs text-muted-foreground">
                      Usuarios de demostración (clic para autocompletar · clave: demo1234):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {DEMO.map((u) => (
                        <Button
                          key={u.cedula}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCedula(u.cedula);
                            setClave('demo1234');
                            setError('');
                          }}
                        >
                          {u.rol}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Lock className="size-5 text-primary" /> Recuperar contraseña
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    La recuperación de la contraseña la gestiona el{' '}
                    <strong className="text-foreground">administrador</strong> del
                    módulo, que administra los accesos. Comuníquese con él para el
                    restablecimiento.
                  </p>
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setModo('login')}
                    >
                      Volver al ingreso
                    </Button>
                  </div>
                </CardContent>
              </>
            )}
          </Card>

          {onPortal && (
            <div className="mt-4 text-center">
              <Button type="button" variant="link" onClick={onPortal}>
                Portal ciudadano (radicar notificación disciplinaria / consultar estado)
              </Button>
            </div>
          )}

          <p className="mt-2 text-center text-xs text-muted-foreground">
            Módulo Disciplinario
          </p>
        </div>
      </div>
    </div>
  );
}
